import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';

import { ConfirmDialog, ErrorState, LoadingState } from '@/presentation/components/index.js';
import type { UserController } from '@/presentation/controllers/user-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useUsers } from '@/presentation/hooks/use-users.js';
import { UserFormDialog } from '@/presentation/pages/users/components/dialogs/user-form-dialog.js';
import { UserListTable } from '@/presentation/pages/users/components/user-list-table.js';
import { resolveCanManageUsers } from '@/presentation/pages/users/utils/resolve-can-manage-users.js';
import { resolveUserConfirmDialog, type UserConfirmAction } from '@/presentation/pages/users/utils/resolve-user-confirm-dialog.js';
import { BRAND_ORANGE, BRAND_RED } from '@/shared/constants/brand.js';

interface Props {
    controller: UserController;
}

export function UserListPage({ controller }: Props) {
    const { translate, useAuthStore } = useApp();
    const me = useAuthStore((s) => s.user);
    const {
        users,
        total,
        page,
        isLoading,
        error,
        search,
        setSearch,
        profileFilter,
        setProfileFilter,
        statusFilter,
        setStatusFilter,
        setPage,
        limit,
        setLimit,
        reload,
        blockUser,
        unblockUser,
        resetUserPassword
    } = useUsers(controller);
    const [confirmAction, setConfirmAction] = useState<UserConfirmAction | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [dialogUserId, setDialogUserId] = useState<string | undefined>(undefined);
    const [toast, setToast] = useState('');

    const openCreate = () => {
        setDialogMode('create');
        setDialogUserId(undefined);
        setDialogOpen(true);
    };

    const openEdit = (userId: string) => {
        setDialogMode('edit');
        setDialogUserId(userId);
        setDialogOpen(true);
    };

    const handleSaved = (message: string) => {
        reload();
        setToast(message);
        setTimeout(() => setToast(''), 4000);
    };

    const canManage = resolveCanManageUsers(me);

    const handleConfirm = async () => {
        if (!confirmAction) {
            return;
        }
        setActionLoading(true);
        if (confirmAction.type === 'block') {
            await blockUser(confirmAction.user.id);
        } else if (confirmAction.type === 'unblock') {
            await unblockUser(confirmAction.user.id);
        } else {
            await resetUserPassword(confirmAction.user.id);
        }
        setActionLoading(false);
        setConfirmAction(null);
    };

    const gradientBtn = {
        textTransform: 'none' as const,
        background: `linear-gradient(to right, ${BRAND_ORANGE}, ${BRAND_RED})`,
        color: 'white',
        '&:hover': { opacity: 0.9, background: `linear-gradient(to right, ${BRAND_ORANGE}, ${BRAND_RED})` }
    };

    const confirmDialogOptions = resolveUserConfirmDialog(confirmAction, translate);

    if (isLoading && users.length === 0) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }

    return (
        <Box sx={{ p: 3 }}>
            {toast && (
                <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, bgcolor: 'success.main', color: 'white', px: 2, py: 1.5, borderRadius: 2, boxShadow: 3 }}>
                    {toast}
                </Box>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                        {translate('users.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {total > 0 ? translate('users.usersFound', { count: total }) : ''}
                    </Typography>
                </Box>
                {canManage && (
                    <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate} disableElevation sx={gradientBtn}>
                        {translate('users.new')}
                    </Button>
                )}
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                <TextField
                    placeholder={translate('users.searchByNameOrEmail')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={16} />
                            </InputAdornment>
                        )
                    }}
                />
                <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} displayEmpty sx={{ minWidth: 160 }}>
                    <MenuItem value="">{translate('users.allStatuses')}</MenuItem>
                    <MenuItem value="ATIVO">{translate('users.active')}</MenuItem>
                    <MenuItem value="BLOQUEADO">{translate('users.blocked')}</MenuItem>
                </Select>
                <Select size="small" value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)} displayEmpty sx={{ minWidth: 160 }}>
                    <MenuItem value="">{translate('users.allProfiles')}</MenuItem>
                    {['ADM', 'PMO', 'CF', 'GPA', 'GPC', 'LT', 'DEV', 'UC'].map((p) => (
                        <MenuItem key={p} value={p}>
                            {translate(`profiles.${p}`)}
                        </MenuItem>
                    ))}
                </Select>
            </Stack>

            <UserListTable
                users={users}
                total={total}
                page={page}
                limit={limit}
                canManage={canManage}
                meId={me?.id}
                onEdit={openEdit}
                onPageChange={setPage}
                onLimitChange={setLimit}
                onConfirmAction={setConfirmAction}
            />

            <ConfirmDialog
                open={!!confirmAction}
                title={confirmDialogOptions.title}
                message={confirmDialogOptions.message}
                confirmLabel={confirmDialogOptions.confirmLabel}
                onConfirm={handleConfirm}
                onClose={() => setConfirmAction(null)}
                loading={actionLoading}
                confirmColor={confirmDialogOptions.confirmColor}
            />

            <UserFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={handleSaved} controller={controller} mode={dialogMode} userId={dialogUserId} />
        </Box>
    );
}
